const LETTER_OR_DIGIT_REGEX = /[0-9A-Za-z\u00C0-\u024F]/;
const NUMBER_REGEX = /[-+]?\d[\d\s.,'\u00A0-]*\d|[-+]?\d/g;

function isAlphaNumeric(char: string | undefined): boolean {
  if (!char) {
    return false;
  }
  return LETTER_OR_DIGIT_REGEX.test(char);
}

function formatMatch(match: string): string {
  if (!match.trim()) {
    return match;
  }

  let value = match.replace(/\u00A0/g, ' ').trim();
  if (!value) {
    return match;
  }

  let sign = '';
  if (value.startsWith('-') || value.startsWith('+')) {
    sign = value[0];
    value = value.slice(1);
  }

  const hasDot = value.includes('.');
  const hasComma = value.includes(',');
  let decimalSeparator = '';

  if (hasDot && hasComma) {
    decimalSeparator =
      value.lastIndexOf('.') > value.lastIndexOf(',') ? '.' : ',';
  } else if (hasComma) {
    const parts = value.split(',');
    if (parts.length === 2 && parts[1].replace(/[^0-9]/g, '').length > 0) {
      decimalSeparator = ',';
    }
  } else if (hasDot) {
    const parts = value.split('.');
    if (parts.length === 2 && parts[1].replace(/[^0-9]/g, '').length > 0) {
      decimalSeparator = '.';
    }
  }

  let integerPart = value;
  let fractionalPart = '';

  if (decimalSeparator) {
    const sepIndex = value.lastIndexOf(decimalSeparator);
    integerPart = value.slice(0, sepIndex);
    fractionalPart = value.slice(sepIndex + 1).replace(/[^0-9]/g, '');
  }

  integerPart = integerPart.replace(/[^0-9]/g, '');

  if (!integerPart) {
    return match;
  }

  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const formattedFractional = fractionalPart ? `,${fractionalPart}` : '';

  return `${sign}${formattedInteger}${formattedFractional}`;
}

export function formatNumbersInText(content: string): string {
  if (!content) {
    return content;
  }

  return content.replace(NUMBER_REGEX, (match, offset: number, full) => {
    const startIndex = offset as number;
    const endIndex = startIndex + match.length;
    const prevChar = startIndex > 0 ? full[startIndex - 1] : undefined;
    const nextChar = endIndex < full.length ? full[endIndex] : undefined;

    if (isAlphaNumeric(prevChar) && isAlphaNumeric(nextChar)) {
      return match;
    }

    const formatted = formatMatch(match);
    return formatted;
  });
}

export function formatNumbersInHtml(html: string): string {
  if (!html) {
    return html;
  }

  const canUseDomParser =
    typeof window !== 'undefined' && typeof DOMParser !== 'undefined';

  if (!canUseDomParser) {
    return formatNumbersInText(html);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);

  let currentNode = walker.nextNode();
  while (currentNode) {
    const textNode = currentNode as Text;
    textNode.textContent = formatNumbersInText(textNode.textContent ?? '');
    currentNode = walker.nextNode();
  }

  return doc.body.innerHTML;
}
